import { AppSidebar, type SidebarGroup } from '@/components/layout/AppSidebar';
import { SidebarOpenerButton } from '@/components/layout/SidebarOpenerButton';
import { UserChip } from '@/components/layout/UserChip';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { useIsMobile } from '@/lib/useIsMobile';
import { useSidebarCollapsed } from '@/lib/useSidebarCollapsed';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import {
  Activity,
  BarChart3,
  BookOpen,
  Bot,
  Cable,
  Database,
  FileCode2,
  FileText,
  FileUp,
  Files,
  Filter,
  FolderOpen,
  HardDrive,
  History,
  KeyRound,
  KeySquare,
  LayoutDashboard,
  Lightbulb,
  Link2,
  ListChecks,
  Mail,
  MessageSquare,
  Monitor,
  Network,
  Palette,
  ScrollText,
  Server,
  ShieldBan,
  ShieldCheck,
  Sparkles,
  Users,
  Wrench,
} from 'lucide-react';
import { useEffect, useRef } from 'react';

const SIDEBAR_GROUPS: SidebarGroup[] = [
  {
    items: [{ label: '대시보드', to: '/admin', icon: LayoutDashboard }],
  },
  {
    heading: 'LLM',
    collapsible: true,
    defaultOpen: true,
    items: [
      { label: '엔드포인트', to: '/admin/llm/endpoints', icon: Sparkles },
      { label: '본딩', to: '/admin/llm/bondings', icon: Link2 },
    ],
  },
  {
    heading: '리소스',
    collapsible: true,
    defaultOpen: true,
    items: [
      { label: '사용 현황', to: '/admin/resource-usage', icon: Monitor },
      { label: '호스트 관리', to: '/admin/resource-hosts', icon: Server },
    ],
  },
  {
    heading: '권한',
    collapsible: true,
    defaultOpen: true,
    items: [
      { label: '그룹', to: '/admin/roles', icon: ShieldCheck },
      { label: '사용자 속성 정의', to: '/admin/permissions/attributes', icon: ListChecks },
      { label: '자동 부여 룰', to: '/admin/permissions/rules', icon: Filter },
    ],
  },
  {
    heading: '운영',
    collapsible: true,
    defaultOpen: true,
    items: [
      { label: '채팅 로그', to: '/admin/chat-log', icon: ScrollText },
      { label: '금칙어', to: '/admin/banned-keywords', icon: ShieldBan },
      { label: '사용 통계', to: '/admin/usage-stats', icon: BarChart3 },
      { label: '사용자', to: '/admin/users', icon: Users },
      { label: '인증', to: '/admin/auth', icon: ShieldCheck },
      { label: '시스템 프롬프트', to: '/admin/system-prompts', icon: FileText },
      { label: 'SMTP 설정', to: '/admin/smtp', icon: Mail },
      { label: '브랜딩', to: '/admin/branding', icon: Palette },
      { label: '패밀리 사이트', to: '/admin/family-sites', icon: Network },
      { label: '데이터 소스', to: '/admin/data-sources', icon: Database },
      { label: '파일 저장소', to: '/admin/file-storage', icon: HardDrive },
      { label: 'API', to: '/admin/http-endpoints', icon: Cable },
      { label: 'API 인증', to: '/admin/api-keys', icon: KeyRound },
      { label: '라이선스', to: '/admin/licenses', icon: KeySquare },
      { label: 'Skill', to: '/admin/skills', icon: Wrench },
      { label: '에이전트 (외부 노출)', to: '/admin/agents', icon: Bot },
      { label: '경험에서 배운 점', to: '/admin/skill-lessons', icon: Lightbulb },
    ],
  },
  {
    heading: '지식 위키',
    collapsible: true,
    defaultOpen: true,
    items: [
      { label: '대시보드', to: '/admin/wikis', icon: BookOpen },
      { label: '시스템 프롬프트', to: '/admin/wikis/config', icon: ScrollText },
      { label: '카테고리', to: '/admin/wikis/categories', icon: FolderOpen },
      { label: '자료', to: '/admin/wikis/documents', icon: FileUp },
      { label: '페이지', to: '/admin/wikis/pages', icon: Files },
      { label: '건강검진', to: '/admin/wikis/health-check', icon: Activity },
      { label: '외부 sync 로그', to: '/admin/wikis/external-sync-logs', icon: History },
    ],
  },
  {
    heading: '슬라이드',
    collapsible: true,
    defaultOpen: true,
    items: [{ label: '시스템 프롬프트', to: '/admin/slides/config', icon: ScrollText }],
  },
  {
    heading: '응답 제어 모듈',
    collapsible: true,
    defaultOpen: true,
    items: [{ label: '프롬프트 관리', to: '/admin/response-controls/prompts', icon: ScrollText }],
  },
  {
    heading: '감사',
    collapsible: true,
    defaultOpen: true,
    items: [
      { label: '로그', to: '/admin/audit', icon: Activity },
      { label: '저장된 쿼리', to: '/admin/saved-queries', icon: FileCode2 },
      { label: '첨부파일', to: '/admin/file-uploads', icon: Files },
      { label: '서버 작업', to: '/admin/server-jobs', icon: ListChecks },
    ],
  },
];

export function AdminLayout() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed, toggle } = useSidebarCollapsed();
  const isMobile = useIsMobile();
  const mobileInitialCollapsedRef = useRef(false);
  useEffect(() => {
    if (isMobile && !mobileInitialCollapsedRef.current && !collapsed) {
      mobileInitialCollapsedRef.current = true;
      toggle();
    }
    if (!isMobile) mobileInitialCollapsedRef.current = false;
  }, [isMobile, collapsed, toggle]);

  // 모바일 + 사이드바 열림 + 라우트 변경 → 자동 close.
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      if (isMobile && !collapsed) toggle();
    }
  }, [location.pathname, isMobile, collapsed, toggle]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate({ to: '/login' });
      return;
    }
    if (user && user.role !== 'admin') {
      navigate({ to: '/chat' });
    }
  }, [isLoading, isAuthenticated, user, navigate]);

  if (isLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground-muted text-sm">
        로딩 중…
      </div>
    );
  }

  const sidebarOpen = !collapsed;
  const utilityLinks = [
    { label: '채팅', to: '/chat', icon: MessageSquare },
    { label: '문서', to: '/wikis/graph', icon: BookOpen },
  ];
  const showOpenerBar = collapsed || isMobile;
  return (
    <ThemeProvider>
      <div className="h-[100dvh] flex bg-background text-foreground overflow-hidden">
        {sidebarOpen && !isMobile ? (
          <AppSidebar
            brand="innermate"
            groups={SIDEBAR_GROUPS}
            activePath={location.pathname}
            width={240}
            utilityLinks={utilityLinks}
            footer={<UserChip allowThemeToggle />}
            onClose={toggle}
          />
        ) : null}
        <main className={cn('flex-1 flex flex-col min-w-0 min-h-0 relative')}>
          {/* 모바일/접힘 상태 한정 — 페이지 위 sticky top bar 에 햄버거. */}
          {showOpenerBar ? (
            <div className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background px-3">
              <SidebarOpenerButton />
            </div>
          ) : null}
          <Outlet />
        </main>
        {sidebarOpen && isMobile ? (
          <>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop 은 사이드바 헤더 X 버튼으로도 닫을 수 있음 */}
            <div
              role="button"
              tabIndex={-1}
              aria-label="사이드바 닫기"
              onClick={toggle}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px]"
            />
            <div className="fixed inset-y-0 left-0 z-50 flex shadow-2xl">
              <AppSidebar
                brand="innermate"
                groups={SIDEBAR_GROUPS}
                activePath={location.pathname}
                width={240}
                utilityLinks={utilityLinks}
                footer={<UserChip allowThemeToggle />}
                onClose={toggle}
              />
            </div>
          </>
        ) : null}
      </div>
    </ThemeProvider>
  );
}
